import React, { useEffect, useMemo, useState } from 'react';

export type SeoModel = {
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  canonicalUrl?: string;
  noindex?: boolean;
  nofollow?: boolean;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  generateJsonLd?: boolean;
};

type Props = {
  value: any; // current post model (can contain SEO fields)
  slug: string;
  onChange: (patch: Partial<SeoModel>) => void;
};

const label = 'text-xs font-medium text-gray-600';
const input = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500';
const textarea = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 min-h-[84px] resize-y';

function clampLen(s: string, n: number) { return (s || '').trim().slice(0, n); }

function extractTextBlocks(blocks: any[]): string[] {
  try {
    return (blocks||[])
      .filter((b:any)=> b && (b.type||'').toLowerCase().includes('paragraph'))
      .map((b:any)=> (b.text||'').toString());
  } catch { return []; }
}

function keywordSuggest(text: string, max = 8): string[] {
  const words = (text||'').toLowerCase().replace(/[^a-z0-9\s]/g,' ').split(/\s+/).filter(Boolean);
  const stop = new Set('the a an and or for with from this that you your our into of to in on by is are was were be been have has it we i he she they them as at not no but if then than over under within without about can will just more most less least best worst new latest how what why where when who which vs vs.'.split(' '));
  const freq = new Map<string, number>();
  for (const w of words) {
    if (w.length < 4 || stop.has(w)) continue;
    freq.set(w, (freq.get(w)||0)+1);
  }
  return Array.from(freq.entries()).sort((a,b)=> b[1]-a[1]).slice(0, max).map(([w])=> w);
}

const SeoTools: React.FC<Props> = ({ value, slug, onChange }) => {
  const blocks = (value?.blocks || []) as any[];
  const firstPara = useMemo(()=> extractTextBlocks(blocks)[0] || '', [blocks]);

  const titleCount = (value?.seoTitle || value?.title || '').length;
  const descCount = (value?.seoDescription || firstPara || '').length;

  const makeTitle = () => {
    const base = value?.title || '';
    onChange({ seoTitle: clampLen(base, 60) });
  };
  const makeDescription = () => {
    const base = firstPara || '';
    onChange({ seoDescription: clampLen(base, 155) });
  };
  const makeKeywords = () => {
    const joined = [value?.title, extractTextBlocks(blocks).join(' ')].join(' ');
    onChange({ seoKeywords: keywordSuggest(joined) });
  };

  const jsonLd = useMemo(()=>{
    if (!value?.generateJsonLd) return '';
    const url = value?.canonicalUrl || `/posts/${slug || ''}`;
    const img = value?.ogImage || value?.cover || '';
    const data = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: value?.seoTitle || value?.title,
      description: value?.seoDescription || firstPara,
      image: img ? [img] : undefined,
      mainEntityOfPage: url,
      author: { '@type': 'Organization', name: 'TinyCMS' },
      dateModified: new Date().toISOString(),
    } as any;
    return JSON.stringify(data, null, 2);
  }, [value?.generateJsonLd, value?.seoTitle, value?.title, value?.seoDescription, value?.cover, value?.ogImage, value?.canonicalUrl, firstPara, slug]);

  return (
    <div className="space-y-4">
      <div>
        <label className={label}>Meta title</label>
        <div className="mt-1 grid grid-cols-[1fr_auto] gap-2">
          <input className={input} value={value?.seoTitle || ''} onChange={(e)=> onChange({ seoTitle: e.target.value })} placeholder="Up to ~60 characters" />
          <button type="button" className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50" onClick={makeTitle}>Auto</button>
        </div>
        <div className={`mt-1 text-xs ${titleCount<=60?'text-gray-500':'text-amber-600'} `}>{titleCount}/60</div>
      </div>
      <div>
        <label className={label}>Meta description</label>
        <textarea className={textarea} value={value?.seoDescription || ''} onChange={(e)=> onChange({ seoDescription: e.target.value })} placeholder="~155 characters" />
        <div className={`mt-1 text-xs ${descCount<=155?'text-gray-500':'text-amber-600'} `}>{descCount}/155</div>
        <div className="mt-2">
          <button type="button" className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50" onClick={makeDescription}>Generate from content</button>
        </div>
      </div>
      <div>
        <label className={label}>Keywords</label>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {(value?.seoKeywords||[]).map((k:string, i:number)=>(
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-xs ring-1 ring-gray-200">{k}</span>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
          <input className={input} placeholder="Comma-separated keywords" value={(value?.seoKeywords||[]).join(', ')} onChange={(e)=> onChange({ seoKeywords: e.target.value.split(',').map(s=>s.trim()).filter(Boolean) })} />
          <button type="button" className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50" onClick={makeKeywords}>Extract</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={label}>Canonical URL</label>
          <input className={input} placeholder="https://example.com/posts/slug" value={value?.canonicalUrl || ''} onChange={(e)=> onChange({ canonicalUrl: e.target.value })} />
        </div>
        <div>
          <label className={label}>Twitter card</label>
          <select className={input} value={value?.twitterCard || 'summary_large_image'} onChange={(e)=> onChange({ twitterCard: e.target.value as any })}>
            <option value="summary_large_image">Summary with large image</option>
            <option value="summary">Summary</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 items-center">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!value?.noindex} onChange={(e)=> onChange({ noindex: e.target.checked })} />
          noindex
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!value?.nofollow} onChange={(e)=> onChange({ nofollow: e.target.checked })} />
          nofollow
        </label>
      </div>
      <div>
        <label className={label}>Open Graph image URL</label>
        <div className="mt-1 grid grid-cols-[1fr_auto_auto] gap-2">
          <input className={input} value={value?.ogImage || ''} onChange={(e)=> onChange({ ogImage: e.target.value })} placeholder="https://..." />
          <button type="button" className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50" onClick={()=> onChange({ ogImage: value?.cover || '' })}>Use cover</button>
          <a href="/media" target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1.5 text-xs hover:bg-gray-50">Open gallery</a>
        </div>
      </div>
      <div className="pt-2 border-t border-gray-200">
        <label className={label}>Structured data (JSON-LD)</label>
        <div className="mt-2 inline-flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!value?.generateJsonLd} onChange={(e)=> onChange({ generateJsonLd: e.target.checked })} />
            Generate Article JSON-LD
          </label>
        </div>
        {jsonLd && (
          <pre className="mt-2 whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 overflow-auto max-h-48">{jsonLd}</pre>
        )}
      </div>
    </div>
  );
};

export default SeoTools;
